import React, { useMemo, memo } from 'react';
import { Popover } from 'antd';
import { Typography } from 'components/common';
import { CallCenterAgent } from '../../../../redux/slices/agents.slice';
import { Utils } from 'utils';
import { getInitials } from 'utils/getInitials';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { shallowEqual } from 'react-redux';
import {
    AvailableIcon,
    OnCallIcon,
    LoggedOutIcon,
} from 'assets';
import './agentListItem.scss';

interface AgentListItemProps {
    agent: CallCenterAgent;
    stateText: string;
    totalAnsweredCalls?: number | string;
}

const STATUS_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
    'Available': { icon: AvailableIcon, label: 'Available', color: '#52c41a' },
    'Ringing': { icon: OnCallIcon, label: 'Ringing', color: '#1890ff' },
    'In a queue call': { icon: OnCallIcon, label: 'In Call', color: '#ff4d4f' },
    'Logged Out': { icon: LoggedOutIcon, label: 'Logged Out', color: '#8c8c8c' },
} as const;

const DEFAULT_STATUS = STATUS_CONFIG['Logged Out'];

const TEXT_COLOR_MUTED = { color: '#8c8c8c' };
const TEXT_STYLE_UPPERCASE = {
    color: '#8c8c8c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem'
};

const AgentListItem: React.FC<AgentListItemProps> = ({
    agent,
    stateText,
    totalAnsweredCalls = 0
}) => {
    const queues = useSelector(
        (state: RootState) => state.apps.data.queue || [],
        shallowEqual
    );

    const status = useMemo(
        () => STATUS_CONFIG[stateText] || DEFAULT_STATUS,
        [stateText]
    );

    const queueNames = useMemo(() => {
        if (!agent.queue || agent.queue.length === 0) return [];
        
        const queueMap = new Map(queues.map(q => [q.extension, q.name]));
        return agent.queue.map(ext => ({
            id: ext,
            name: queueMap.get(ext) || ext,
            extension: ext,
        }));
    }, [agent.queue, queues]);

    const tiers = useMemo(() => agent.level || [], [agent.level]);

    const initials = useMemo(() => getInitials(agent.name), [agent.name]);

    const popoverContent = useMemo(() => (
        <div className="agent-popover">
            <div className="agent-popover-header">
                <div className="agent-avatar-large">
                    {initials}
                </div>
                <div className="agent-info">
                    <Typography variant="subHeading-1">{agent.name}</Typography>
                    <Typography variant="small-text-4" style={TEXT_COLOR_MUTED}>
                        Ext. {agent.extension}
                    </Typography>
                </div>
                <div
                    className="agent-status-badge"
                    style={{
                        backgroundColor: `${status.color}20`,
                        color: status.color
                    }}
                >
                    {status.label}
                </div>
            </div>

            <div className="agent-popover-content">
                <div className="agent-stat-row">
                    <Typography variant="small-text-4" style={TEXT_COLOR_MUTED}>
                        Total Answered
                    </Typography>
                    <Typography variant="text-2" style={{ fontWeight: 600 }}>
                        {totalAnsweredCalls} calls
                    </Typography>
                </div>

                <div className="agent-stat-row">
                    <Typography variant="small-text-4" style={TEXT_COLOR_MUTED}>
                        DND Status
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {agent.dndEnabled ? (
                            <>
                                <i className="bx bx-moon" style={{ fontSize: '14px', color: '#ff4d4f' }} />
                                <Typography variant="text-2" style={{ fontWeight: 600, color: '#ff4d4f' }}>
                                    Enabled
                                </Typography>
                            </>
                        ) : (
                            <>
                                <i className="bx bx-sun" style={{ fontSize: '14px', color: '#52c41a' }} />
                                <Typography variant="text-2" style={{ fontWeight: 600, color: '#52c41a' }}>
                                    Disabled
                                </Typography>
                            </>
                        )}
                    </div>
                </div>

                {tiers.length > 0 && (
                    <div className="agent-section">
                        <Typography variant="small-text-4" style={TEXT_STYLE_UPPERCASE}>
                            Tiers
                        </Typography>
                        <div className="agent-tags">
                            {tiers.map((tier) => {
                                const tierColor = Utils.generateNameSpaceColor(`Tier ${tier}`);
                                return (
                                    <span
                                        key={tier}
                                        className="agent-tier-tag"
                                        style={{
                                            backgroundColor: `${tierColor}20`,
                                            color: tierColor,
                                        }}
                                    >
                                        Tier {tier}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}

                {queueNames.length > 0 && (
                    <div className="agent-section">
                        <Typography variant="small-text-4" style={TEXT_STYLE_UPPERCASE}>
                            Queues
                        </Typography>
                        <div className="agent-queues-list">
                            {queueNames.map((queue) => {
                                const queueColor = Utils.generateNameSpaceColor(queue.name);
                                return (
                                    <div key={queue.id} className="agent-queue-item">
                                        <div
                                            className="agent-queue-dot"
                                            style={{ backgroundColor: queueColor }}
                                        />
                                        <Typography variant="small-text-4">{queue.name}</Typography>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {queueNames.length === 0 && tiers.length === 0 && (
                    <Typography variant="small-text-4" style={{ ...TEXT_COLOR_MUTED, fontStyle: 'italic' }}>
                        No queues assigned
                    </Typography>
                )}
            </div>
        </div>
    ), [agent, status, totalAnsweredCalls, tiers, queueNames, initials]);

    const displayName = useMemo(
        () => agent.name.length > 10 ? agent.name : agent.extension,
        [agent.name, agent.extension]
    );

    const tierBadges = useMemo(() => {
        if (tiers.length === 0) return null;
        
        const visibleTiers = tiers.slice(0, 3);
        const remainingCount = tiers.length - 3;
        
        return (
            <div className="agent-tiers">
                {visibleTiers.map((tier) => {
                    const tierColor = Utils.generateNameSpaceColor(`Tier ${tier}`);
                    return (
                        <span
                            key={tier}
                            className="agent-tier-badge"
                            style={{
                                backgroundColor: `${tierColor}20`,
                                color: tierColor,
                            }}
                        >
                            T{tier}
                        </span>
                    );
                })}
                {remainingCount > 0 && (
                    <Typography variant="small-text-4" style={{ color: '#8c8c8c', marginLeft: '4px' }}>
                        +{remainingCount}
                    </Typography>
                )}
            </div>
        );
    }, [tiers]);

    return (
        <Popover
            content={popoverContent}
            trigger="hover"
            placement="right"
            overlayClassName="agent-popover-overlay"
            mouseEnterDelay={0.2}
            overlayStyle={{ width: 'auto' }}
        >
            <div className="agent-row-wrapper">
                <div className="agent-row">
                    <div 
                        className="agent-status-dot" 
                        style={{ backgroundColor: status.color }} 
                    />

                    <div className="agent-avatar">
                        {initials}
                    </div>

                    <div className="agent-main" title={`${agent.extension} - ${agent.name}`}>
                        <Typography
                            variant="subHeading-1"
                            style={{ fontWeight: 500 }}
                            className="agent-name"
                        >
                            {displayName}
                        </Typography>
                    </div>

                    <div className="agent-right-section">
                        <div className="agent-stats">
                            {agent.dndEnabled && (
                                <div className="agent-dnd-indicator" title="Do Not Disturb Enabled">
                                    <i className="bx bx-moon" style={{ fontSize: '14px', color: '#ff4d4f' }} />
                                </div>
                            )}

                            <div className="agent-stat">
                                <i className="bx bxs-phone" style={{ fontSize: '14px' }} />
                                <Typography variant="small-text-4">{totalAnsweredCalls}</Typography>
                            </div>

                            {queueNames.length > 0 && (
                                <div className="agent-stat">
                                    <i className="bx bx-group" style={{ fontSize: '14px' }} />
                                    <Typography variant="small-text-4">{queueNames.length}</Typography>
                                </div>
                            )}

                            {tierBadges}
                        </div>

                        <i className="bx bx-chevron-right agent-arrow" />
                    </div>
                </div>
            </div>
        </Popover>
    );
};

export default memo(AgentListItem);
